<template>
  <div class="drug-register-view view">
    <ReceivePOMModal v-if="displayReceivePOMModal" @close="closeModal" />
    <CheckoutPOMModal v-if="displayCheckoutPOMModal" :bag="selectedBag" @close="closeCheckout" @successful="onCheckoutSuccess" />
    <SignatureModal 
        v-show="displayActionModal"
        :payload="toAction" 
        :endpoint="singleActionEndpoint" 
        @close="close" 
        @successful="isSuccessful">
    </SignatureModal>
    <div class="actions-panel panel" style="width: fit-content; display: flex; flex-direction: column;">
      <h3 class="panel-header">Actions</h3>
      <button @click="displayReceivePOMModal = true" class="actions-menu-item" v-tooltip.right="'Add a patient bag'">Add Patient Bag</button>
    </div>
    <div class="panel" v-if="$store.state.user.role.permissions.includes('can_view_drug_register') && $store.state.user.patient_own_bags_active">
      <h3 class="panel-header">Patient Bags</h3>
      <div class="drug-register-table table-container">
        <div class="filters" style="display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end;">
          <button class="button submit-button" @click="loadBags()">Refresh</button>
        </div>

        <div v-if="!loading" style="display: flex; align-items: start; overflow-y: auto; height: 100%;">
          <table class="data-table" cellspacing="0" v-if="bags.length > 0">
            <tr class="table-headers">
              <th style="width: 50px"></th>
              <th style="width: 250px;">
                <h3>{{ $resident({ capitalize: true }) }} Name</h3>
              </th>
              <th>
                <h3>Check-in Date</h3>
              </th>
              <th>
                <h3>Completed By</h3>
              </th>
              <th>
                <h3>Medication Count</h3>
              </th>
            </tr>
            
            <tbody class="table-data" v-for="(bag, index) in bags" :key="index">
              <tr @click="showContent(bag, index)" :class="[ bag.display ? 'open-transfer-row': '']">
                <td>
                  <i v-if="bag.display" class="pi pi-angle-down"></i>
                  <i v-else class="pi pi-angle-right"></i>
                </td>
                <td>{{ getPatientName(bag.patient) }}</td>
                <td>{{ formatDate(bag.check_in_date) }}</td>
                <td>{{ getExecutorName(bag.executor) }}</td>
                <td>{{ bag.items ? bag.items.length : 0 }} Item(s)</td>
              </tr>
              
              <tr :ref="'expand-'+index" :id="'expand-'+index" class="expanded-content" style="display: none;">
                <td colspan="5">
                  <div class="expanded-transfer-container">
                    
                    <div class="transfer-specs-container">
                        <div class="status-field">
                           <p class="field-title">Status</p>
                           <p class="field-value correct-status-color" style='text-align: center'>Active</p>
                        </div>
                        <div class="type-field">
                           <p class="field-title">Type</p>
                           <p class="field-value received-status-color" style='text-align: center'>Patient Bag</p>
                        </div>
                        <div class="date-received-field">
                           <p class="field-title">Check-in Date</p>
                           <p class="field-value">{{ formatDate(bag.check_in_date) || 'N/A' }}</p>
                        </div>
                        <div class="ordered-by-field">
                           <p class="field-title">{{ $resident({ capitalize: true }) }}</p>
                           <p class="field-value">{{ getPatientName(bag.patient) || 'N/A' }}</p>
                        </div>
                        <div class="fulfilled-by-field">
                           <p class="field-title">Prepared By</p>
                           <p class="field-value">{{ getExecutorName(bag.executor) || 'N/A' }}</p>
                        </div>
                        <div class="ordered-from-field" v-if="bag.bag_id">
                           <p class="field-title">Bag ID</p>
                           <p class="field-value">{{ bag.bag_id }}</p>
                        </div>
                    </div>

                    <div class="transfer-notes-container">
                        <p class="field-title">Notes</p>
                        <div class="field-notes" >
                            <p>{{ bag.notes || 'None' }}</p>
                        </div>
                    </div>
                    
                    <div class="transfer-drugs-container">
                      <p class="field-title">Medications</p>
                      <div class="drug-field-tables" style="width: 95%; border: 2px solid rgb(182, 182, 182); border-radius: 4px; height: 300px; overflow-y: auto; background-color: white;">
                        <table style="padding: 0px 5px; width: 100%; text-align: left; border-collapse: collapse;">
                          <thead>
                            <tr style="position: sticky; top: 0; background: white; z-index: 1; border-bottom: 2px solid #eee;">
                              <th style="padding: 8px; background: white;">Name</th>
                              <th style="padding: 8px; width: 150px; background: white;">Quantity</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(item, key) in bag.items" :key="key" style="height: 35px; border-top: 1px solid #eee;">
                              <td style="padding: 8px; font-size: 0.9em;">
                                {{ item.alias ? (item.alias.generic_name ? '(' + item.alias.generic_name + ') - ' + item.alias.name : item.alias.name) : 'Unknown' }}
                              </td>
                              <td style="padding: 8px; font-size: 0.9em; width: 150px;">{{ item.quantity }}</td>
                            </tr>
                          </tbody>
                        </table>
                        <div v-if="!bag.items || bag.items.length === 0" style="padding: 15px; text-align: center; color: #666;">
                          No medications found in this bag.
                        </div>
                      </div>
                    </div>

                    <div class="transfer-actions-container" style="display: flex; gap: 10px;">
                        <button class="transfer-action-button" style="background-color: #0870DC; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;" @click="checkoutBag(bag)">Checkout Bag</button>
                        <button class="transfer-action-button error-button" @click="reverseBag(bag)">Reverse Bag</button>
                    </div>

                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else style="width: 100%; padding: 40px; text-align: center;">
            <h3>No patient bags found.</h3>
          </div>
        </div>
        <div v-else>
          <i class="pi pi-spin pi-spinner" style="font-size: 3rem !important; margin-top: 4em;"></i>
        </div>
      </div>
    </div>
    <div v-else class="drug-register-table table-container" style="padding-top: 5em; height: 78vh;">
      <h3 style="border: 2px solid black; border-bottom: 2px solid black; width: fit-content; height: fit-content; margin: 0 auto; padding: 5px; border-radius: 4px">You do not have permission to access this feature. Please talk to your admin if you require access.</h3>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SignatureModal from '@/components/modals/SignatureModal.vue';
import ReceivePOMModal from '@/components/modals/ReceivePOMModal.vue';
import CheckoutPOMModal from '@/components/modals/CheckoutPOMModal.vue';

export default {
  name: 'PatientBags',
  components: { 
      SignatureModal,
      ReceivePOMModal,
      CheckoutPOMModal
  },
  data() {
    return {
      bags: [],
      loading: false,
      displayActionModal: false,
      displayReceivePOMModal: false,
      displayCheckoutPOMModal: false,
      selectedBag: null,
      singleActionEndpoint: '',
      toAction: {
          bag_id: '',
          notes: '',
      },
    }
  },
  methods: {
    loadBags() {
      this.loading = true;
      axios.post('/patient-bags', {}, this.$store.state.header).then(res => {
        this.bags = res.data.map(bag => ({ ...bag, display: false }));
        this.loading = false;
      }).catch(err => {
        console.error(err);
        this.loading = false;
      });
    },
    showContent(bag, index) {
      bag.display = !bag.display;
      const el = document.getElementById(`expand-${index}`);
      if (el) {
        el.style.display = bag.display ? 'table-row' : 'none';
      }
    },
    getExecutorName(executor) {
      if (!executor) return 'Unknown';
      if (executor.latest_personal_information) {
        return `${executor.latest_personal_information.firstname || ''} ${executor.latest_personal_information.lastname || ''}`.trim();
      }
      if (executor.personal_information) {
        return `${executor.personal_information.firstname || ''} ${executor.personal_information.lastname || ''}`.trim();
      }
      return 'Unknown';
    },
    reverseBag(bag) {
      this.toAction = {
          bag_id: bag.uuid,
          notes: ''
      };
      this.singleActionEndpoint = '/patient-own-reverse';
      this.displayActionModal = true;
    },
    checkoutBag(bag) {
      this.selectedBag = bag;
      this.displayCheckoutPOMModal = true;
    },
    closeCheckout() {
      this.displayCheckoutPOMModal = false;
      this.selectedBag = null;
    },
    onCheckoutSuccess() {
      this.displayCheckoutPOMModal = false;
      this.selectedBag = null;
      this.loadBags();
    },
    close(value) {
      if(value) {
          this.displayActionModal = false;
          this.loadBags();       
      } else {
          this.displayActionModal = false;
      }
    },
    closeModal(value) {
      this.displayReceivePOMModal = false;
      if(value) {
          this.loadBags();
      }
    },
    isSuccessful(value) {
      this.loadBags();
    },
    getPatientName(patient) {
      if (!patient) return 'Unknown';
      const info = patient.latest_personal_information || patient.personal_information;
      if (!info) return 'Unknown';
      const first = info.firstname || info.first_name || '';
      const last = info.lastname || info.last_name || '';
      return `${first} ${last}`.trim() || 'Unknown';
    },
    formatDate(date) {
      if (!date) return '--';
      const d = new Date(date);
      const pad = (n) => (n < 10 ? '0' + n : n);
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
  },
  mounted() {
    this.loadBags();
  }
}
</script>

<style lang="scss">
@import "../../assets/css/main.scss";
</style>
